import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Imagekit } from "../../Pages/CommonPages/ImageKit";
import { createSlug } from "../../Utils/HelperFunctions";
import defaultProductImage from "../../assets/Svg/default.svg";
import { formatCardPrice } from "../../Utils/cardPrice";

/**
 * NewlyArrivedRow
 *
 * Sarees this store selected inside the backend's rolling window, from
 * get_home_page -> newArrivals. The COUNT drives the layout (CEO spec, 2026-09-03):
 *
 *   2         -> single row of two, no scroll
 *   3, 4 or 5 -> horizontal scroll   (the backend already picked a random five above five)
 *
 * 0 or 1 never reaches here: the backend omits `newArrivals` entirely, so there is no empty
 * state and no heading to suppress.
 *
 * Spec change 2026-09-03: display max 4 -> 5 and the 2x2 grid is GONE. Two is now the only
 * non-scrolling shape, so the grid container exists solely for it.
 *
 * Every card is the same size in every layout — the earlier 2-hero + 3-small arrangement was
 * rejected because the tiles differed.
 *
 * @param {{title?: string, products?: Array, currency?: string}} [data]  the `newArrivals` envelope
 * @param {string} [ownerName]                                           owner's first name
 */

// One fixed ImageKit render for every card, since every card is now the same size. 2x the CSS box
// (184.5px at a 412px viewport), matching the repo's DPR convention (264/132 in PDPProductCard,
// 220/110 in ImageKitThumbNail, 248/124 in AboutHero). 369:492 is exact 3:4, so the delivered
// image matches the aspect-[3/4] box with no rounding drift.
//
// fo-top anchors the crop to the top edge and trims from the bottom — the same choice, and the
// same reason, as ImageKitThumbNail: heads and subjects sit at the top of a garment shot. fo-face
// is deliberately NOT used; it sizes its window relative to the detected face and turns a small
// tile into a headshot with the saree cropped out.
//
// responsive={false} is REQUIRED, not cosmetic. An explicit height pins one aspect ratio, so with
// a srcset on it would be correct at exactly one width and wrong at every other.
const IK_RENDER = { width: 369, height: 492, focus: "top" };

// Cell width for the scroll rail (3, 4 or 5 items), matched to what the 2-case grid yields at the
// SAME breakpoint so a card is identically sized in both shapes: 2 columns on mobile, 4 from md
// up -- i.e. 184.5px at 412px and 253.8px at 1440px. The
// percentages resolve against the track's content box, so both stay correct at any viewport.
//
// Classes rather than an inline width because this now needs a media query, and inline styles
// cannot carry one. Underscores are Tailwind's escape for the spaces CSS calc() requires around
// its - operator; calc(100%-11px) without them is invalid CSS and would drop the declaration.
const RAIL_CARD_CLASS =
  "flex-none w-[calc((100%_-_11px)/2)] md:w-[calc((100%_-_33px)/4)]";

// Scroll progress bar under the rail: a full-width track with a fill that grows left-to-right,
// reaching the whole track at the end of the scroll. Replaces the native scrollbar (which showed a
// fixed-proportion thumb sliding along) and, before that, the pagination dots.
//
// No class is needed to hide the native scrollbar again: index.css:11 already hides every
// scrollbar app-wide via `*::-webkit-scrollbar { display: none }` plus `* { scrollbar-width: none }`.
// The previous revision had to fight that with `[&::-webkit-scrollbar]:block`; removing those
// classes is enough, and re-adding a `no-scrollbar`-style class would be dead weight -- that class
// is not defined anywhere in this repo and never was.
//
// Colours unchanged from the scrollbar revision: wc-border track / ink-900 fill, which is the
// unfilled / filled pair PinboardShelf uses for its own scroll indicator (PinboardShelf.jsx:235-236).
// 4px thickness is also unchanged, and the shimmer already renders an identical static track.
//
// The fill never starts at 0%: a zero-width fill is invisible, and the spec asks for it to "start
// narrow", so progress is mapped onto [MIN, 100] rather than [0, 100]. 10% is a deliberate choice,
// not a measurement -- change this one number to make the resting state wider or narrower.
const FILL_MIN_PERCENT = 10;

// Badge flush in the tile's top-left corner. Top-left radius equals the tile's own
// (rounded-[12px]) so the badge continues the corner instead of sitting on it as a separate shape;
// bottom-left is 0 so it meets the tile's straight left edge square — a pill radius there would
// overlap the 12px corner curve on a ~17px-tall badge and leave no straight edge at all. The
// right-hand radii are half the badge height, written out rather than a 9999px pill: CSS scales
// ALL radii by one factor when any edge's radii exceed its length, which would collapse the 12px
// corner to nothing.
//
// Inline styles on purpose. Tailwind compiles at build time, so an arbitrary value that appears
// nowhere else in src/ is absent from the bundle; only classes already in use are safe as classes.
const BADGE_STYLE = {
  display: "inline-block",
  whiteSpace: "nowrap",
  fontWeight: 500,
  lineHeight: 1,
  color: "#C73F21",
  background: "rgba(255,255,255,.94)",
  border: "1px solid rgba(199,63,33,.32)",
  boxShadow: "0 1px 5px rgba(31,33,46,.16)",
  borderRadius: "12px 8.5px 8.5px 0",
  fontSize: "9px",
  padding: "3.9px 7px 2.1px",
  letterSpacing: ".025em",
};

function NewArrivalCard({ product, currency, onOpen, style, className = "", priority = "auto" }) {
  const { t } = useTranslation();
  // Short name (prd_short_name, 32 chars) in preference to the full name (prd_name, 80), which
  // wrapped to two lines and clipped. Already on the payload -- the query has selected
  // p.prd_short_name AS productShortName since the first build and CoreService maps it onto
  // FeaturedProductsDTO.productShortDescription, so no backend change was needed.
  //
  // The fallback is REQUIRED, not defensive padding, and this is measured rather than inferred:
  // 8 of the 12 sarees on dev have no short name, so two thirds of tiles would render titleless
  // without it. The cause is upstream -- in admin's ProductRequest only `name` carries @NotBlank
  // ("Please provide product name."); `productShortName` has no validation at all, and the
  // entity's @Column(name = "prd_short_name") sets no nullable = false. A product saved without
  // one is entirely valid.
  //
  // .trim() as well as ||, because "" and "   " both have to fall through and only || catches "".
  //
  // Full name is deliberately kept for the URL slug (canonical, and more descriptive for search)
  // and for the image alt text.
  const displayName =
    (product?.productShortName || "").trim() ||
    // Legacy key: the backend used to deliver the short NAME under this
    // short-DESCRIPTION field. Both are sent during the rollout; drop this
    // line once the backend change is live everywhere.
    (product?.productShortDescription || "").trim() ||
    product?.productName ||
    "";

  const price =
    product?.priceDetails?.finalAmount ??
    product?.priceDetails?.sellingPrice ??
    product?.priceDetails?.mrp;
  const mrp = product?.priceDetails?.mrp;
  // Same rule as the listing card (EachProductDetails): strike the MRP only when it is higher.
  const showMrp = mrp && price != null && mrp > price;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`text-left cursor-pointer ${className}`}
      style={style}
    >
      <div className="relative w-full aspect-[3/4] rounded-[12px] overflow-hidden bg-wc-gray shadow-[0_1px_3px_rgba(31,33,46,0.08)]">
        {/* Geometry is forced with Tailwind's important modifier (!w-full etc, v3 `!` prefix — the
            same form CartPage already uses). This is not stylistic: the img is `position:absolute`
            with inset-0, and for an absolutely positioned REPLACED element CSS 2.1 §10.3.7 says
            that when width resolves to `auto` the INTRINSIC width is used and `right` is dropped
            as over-constrained. So the image would render at its natural width, flush left, with
            the tile's bg-wc-gray showing down the right edge — exactly the reported symptom.
            Filling therefore hinges entirely on w-full/h-full winning the cascade, and the plain
            utilities are specificity 0,1,0 — any `.parent img` rule (0,1,1) silently beats them.
            The important modifier makes the fill unconditional instead of merely likely.

            Verified NOT to be the cause here, so this is hardening rather than the diagnosis: the
            CDN returns exactly 369x492 for all four products (originals 452x678, 832x1248,
            832x1248, 1024x1024 — measured), neither image carries baked-in edge whitespace
            (decoded the pixels: 0 near-white columns on either side), and className reaches the
            <img> in all four of the wrapper's render branches. */}
        <Imagekit
          IMGName={product?.productUrl || defaultProductImage}
          alt={product?.productName || ""}
          className="absolute inset-0 !w-full !h-full !object-cover !object-top"
          width={IK_RENDER.width}
          height={IK_RENDER.height}
          transformation={[IK_RENDER]}
          responsive={false}
          eager
          // The first card's image is usually the page's largest paint: fetch it ahead of the
          // rest. The others stay eager (lazy never fires in this horizontal rail, see Imagekit)
          // but at low priority, so they don't compete with it.
          fetchpriority={priority}
        />
        {/* Flush at 0,0 — the tile's overflow-hidden clips it to the same corner curve. */}
        <div className="absolute top-0 left-0 z-10">
          <span style={BADGE_STYLE}>{t("newlyArrived.badge")}</span>
        </div>
      </div>

      {/* min-h-[30px] and line-clamp-2 both stay. The min-height reserves two lines so every card
          is the same height whether its name wraps or not -- that matters more in a rail than the
          whitespace under a short one -- and the clamp still guards the 80-char full name whenever
          the fallback fires. */}
      <p className="text-[12px] font-[400] text-ink-900 leading-[1.25] mt-[7px] min-h-[30px] line-clamp-2">
        {displayName}
      </p>
      {/* Struck-through MRP, then the selling price, as on the listing card. No % OFF.
          Same format as every other product card (Utils/cardPrice): "₹1,140", "₹1,377.50". */}
      <p className="text-[13px] font-[500] text-ink-900 mt-[3px]">
        {showMrp && (
          <span className="line-through text-[11px] font-normal text-ink-500 mr-[4px]">
            {formatCardPrice(mrp, currency)}
          </span>
        )}
        {price != null ? formatCardPrice(price, currency) : ""}
      </p>
    </div>
  );
}

export default function NewlyArrivedRow({ data, ownerName }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const railRef = useRef(null);
  const [fillPercent, setFillPercent] = useState(FILL_MIN_PERCENT);
  // Whether the rail actually overflows. Not always true even in the scroll case: at md the cells
  // drop to 25%, so THREE cards (3 x 253.8 + 22 = 783px) fit inside a 1048px container with
  // nothing to scroll, while five (1313px) still overflow. Without this the bar would sit at a
  // full 100% fill under a row that cannot move.
  const [scrollable, setScrollable] = useState(false);
  const products = Array.isArray(data?.products) ? data.products : [];
  // Display currency off the envelope, absent -> INR. Same contract the listing pages use
  // (ShopByCraft/VerifiedHandloom read `d.currency` from their response), so prices here can
  // never silently render an INR amount with a "$" or vice versa. Resolved to a symbol at the
  // render site.
  const currency = data?.currency || "INR";

  // 2 is the only non-scrolling shape now; 3, 4 and 5 all scroll.
  const isScroll = products.length >= 3;

  const measure = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    // <=1px of overflow is sub-pixel rounding, not a scrollable track.
    if (max <= 1) {
      setScrollable(false);
      setFillPercent(FILL_MIN_PERCENT);
      return;
    }
    setScrollable(true);
    // Clamped because iOS rubber-band scrolling reports scrollLeft outside [0, max].
    const progress = Math.min(1, Math.max(0, el.scrollLeft / max));
    setFillPercent(FILL_MIN_PERCENT + (100 - FILL_MIN_PERCENT) * progress);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el || !isScroll) return undefined;
    el.addEventListener("scroll", measure, { passive: true });
    // The cell width is a percentage, so a resize changes scrollWidth, and at md it can remove the
    // overflow altogether -- the bar has to re-evaluate, not just re-position.
    window.addEventListener("resize", measure);
    measure();
    return () => {
      el.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
    };
  }, [isScroll, measure]);

  // Defensive: the backend already gates 0 and 1 by omitting the key.
  if (products.length < 2) return null;

  const openProduct = (p) =>
    navigate(
      `/product/${createSlug(p?.productName || "product")}/${p?.productId}${
        p?.skuId ? `?skuId=${p.skuId}` : ""
      }`,
    );

  const card = (p, i, style, extraClass) => (
    <NewArrivalCard
      key={p?.skuId || p?.productId}
      product={p}
      currency={currency}
      style={style}
      className={extraClass}
      priority={i === 0 ? "high" : "low"}
      onOpen={() => openProduct(p)}
    />
  );

  // Two shapes now, not three. The 2x2 grid is gone (spec change 2026-09-03), so the grid
  // container serves the 2-case alone and everything from 3 up scrolls. md:grid-cols-4 stays on it
  // deliberately: it makes a 2-case card 253.8px on desktop, the same width the rail gives, so a
  // card is identically sized whichever shape renders.
  //
  // Desktop sizing is done by COLUMN COUNT, not by a max-width. Earlier revisions capped the
  // section (720px, then 400px) because grid-cols-2 against App.js's container grows without limit
  // — that container is an antd Col with PERCENTAGE spans and no max-width (xs=24/md=22/xl=18/
  // xxl=14, so 1080px at a 1440px viewport), which put the tile at 518x691. But a cap forces a
  // choice between whitespace pooled on one side and, with mx-auto, a heading that drifts 180-360px
  // off the 16px left rail every other home-page heading sits on, non-monotonically as the window
  // resizes.
  //
  // Adding columns instead is what the page already does: MyStore's category grid runs
  // grid-cols-4 -> md:5 -> lg:6 -> xl:8 -> 2xl:10 for exactly this reason. The section keeps its
  // full width, so the heading and the grid stay flush at 16px with every other section, there is
  // no leftover to distribute, and the tile lands at (container - 32 - 33) / 4 = 253.8px at 1440.
  //
  // Mobile is untouched: grid-cols-2 is the unprefixed default and md: cannot apply below 768px, so
  // 412px stays at 184.5px exactly as the CEO approved it.
  //
  // Cost, accepted: with only 2 products the desktop row fills 2 of 4 columns and the right half is
  // empty. Keeping the tile size equal across 2/3/4 was the explicit requirement, and a partly
  // filled row is how every other grid on the page behaves when it has few items.

  return (
    <section className="pt-[6px] pb-[10px]">
      {/* 17px/500 is the home page's own section-heading standard (CraftClusterRow, the category
          grid) — not PDPProductRail's 15px/400, and without its 8px gray divider, which no home
          section uses. */}
      <div className="px-4 pb-[11px]">
        <h2 className="text-[17px] font-[500] text-ink-900 tracking-[-0.01em]">
          <Trans
            i18nKey="newlyArrived.heading"
            components={[<span className="text-coral-ink" />]}
          />
        </h2>
        {/* Deliberately claims neither a count nor an order: when the window holds more than four
            the backend shows a RANDOM four, so "the 4 latest" would be untrue, and the same
            wording has to read correctly at 2, 3 and 4. */}
        <div className="text-[11px] text-ink-500 mt-[3px]">
          {t("newlyArrived.sub", {
            name: ownerName || t("newlyArrived.thisStore"),
          })}
        </div>
      </div>

      {isScroll ? (
        <div
          ref={railRef}
          className="flex gap-[11px] overflow-x-auto pt-[2px] px-4 pb-[8px] snap-x snap-mandatory scroll-pl-4"
        >
          {products.map((p, i) =>
            card(p, i, { scrollSnapAlign: "start" }, RAIL_CARD_CLASS),
          )}
        </div>
      ) : null}

      {/* Progress bar. Only in the scroll case, and only when the rail really overflows -- see the
          `scrollable` note above for why those are not the same condition. aria-hidden: it mirrors
          the rail's own scroll position, which assistive tech already exposes, and it is not
          interactive (unlike the dots it replaced, which were tappable buttons).

          mt-[6px] is the app's own spacing after a price line, not a new value: in
          EachProductDetails the element directly following the price is the size row at :187,
          which uses mt-[6px] (its COD tag at :208 uses mt-[5px]). On top of the rail's own
          pb-[8px] that leaves ~14px between the price text and the track. */}
      {isScroll && scrollable && (
        <div
          aria-hidden="true"
          className="mx-4 mt-[6px] h-[4px] rounded-full bg-wc-border overflow-hidden"
        >
          <div
            className="h-full rounded-full bg-ink-900"
            style={{ width: `${fillPercent}%` }}
          />
        </div>
      )}

      {!isScroll ? (
        <div className="px-4 pt-[2px] pb-[8px] grid grid-cols-2 md:grid-cols-4 gap-[11px]">
          {products.map((p, i) => card(p, i))}
        </div>
      ) : null}
    </section>
  );
}
